const focus = (instruction, variable = "skill", options = {}) => ({
  instruction,
  variable,
  ...options,
});

const activity = (
  activityType,
  base,
  focuses,
  {
    preferredResources = [],
    equipment = [],
    participation = ["solo", "social"],
    supervision = "not-required",
    environmentProgressions = [],
  } = {},
) => ({
  activityType,
  base: focus(base, "consistency"),
  levels: {
    beginner: [focuses[0], focuses[1]],
    intermediate: [focuses[1], focuses[2]],
    advanced: [focuses[2], focuses[3]],
  },
  requirements: {
    preferredResources,
    equipment,
    participation,
    supervision,
  },
  environmentProgressions,
});

const environment = (requiresAny, stage, variation) => ({
  requiresAny,
  stage,
  variation,
});

const TECHNICAL = "technical-skill";
const AEROBIC = "rhythmic-aerobic";
const SOCIAL = "social-casual";
const COMBAT = "combat";
const PLAY = "recreational-play";
const CLASS = "structured-class";
const STRENGTH = "strength";
const RECOVERY = "mobility-recovery";

export const WEEKLY_PROGRESSION_SEQUENCE = [
  { stage: "base", role: "Establish the base" },
  { stage: "base", role: "Repeat the base" },
  { stage: "progression-1", role: "Focused progression one" },
  { stage: "base", role: "Return to the familiar base" },
  { stage: "progression-1", role: "Repeat the first focus" },
  { stage: "progression-2", role: "Focused progression two" },
  { stage: "base", role: "Consolidate with the familiar version" },
];

export const MORNING_ROUTINE_PROGRESSIONS = {
  base: focus(
    "Drink water, eat carbohydrate and protein when needed, then move slowly",
    "consistency",
  ),
  "progression-1": focus(
    "Drink water, then take five slow breaths before moving on with the morning",
    "attention",
  ),
  "progression-2": focus(
    "Drink water, then reach both arms overhead for a one-minute reach-to-the-sky stretch",
    "movement",
    { durationOverride: 6 },
  ),
};

export const MIDDAY_ROUTINE_PROGRESSIONS = {
  base: focus("Walk at a comfortable, purposeful pace", "consistency"),
  "progression-1": focus(
    "Walk a different familiar route at the same comfortable pace",
    "environment",
  ),
  "progression-2": focus(
    "Walk with relaxed arm movement and steady breathing",
    "attention",
  ),
};

export const ACTIVITY_PROGRESSION_REPOSITORY = {
  archery: activity(
    TECHNICAL,
    "target practice with a comfortable draw weight",
    [
      focus("group arrows at one comfortable target", "skill"),
      focus("alternate between two familiar target areas", "skill"),
      focus("use a consistent setup and release routine", "attention"),
      focus("practise controlled distance changes", "skill"),
    ],
    { equipment: ["bow", "arrows", "target"], supervision: "range-rules" },
  ),
  badminton: activity(
    TECHNICAL,
    "controlled rallies or footwork practice",
    [
      focus("practise forehand contact and recovery position", "skill"),
      focus("alternate forehand and backhand returns", "skill"),
      focus("add controlled front-to-back footwork", "movement"),
      focus("use a simple serve-and-third-shot pattern", "skill"),
    ],
    { equipment: ["racquet", "shuttle"], preferredResources: ["sport-group"] },
  ),
  baseball: activity(
    TECHNICAL,
    "throwing, catching, or batting practice",
    [
      focus("practise easy catches and accurate short throws", "skill"),
      focus("alternate fielding position and throwing target", "skill"),
      focus("link fielding, transfer, and controlled throw", "skill"),
      focus("use a familiar batting or fielding sequence", "complexity"),
    ],
    { equipment: ["ball", "glove"], preferredResources: ["grass-yard", "sport-group"] },
  ),
  basketball: activity(
    TECHNICAL,
    "shooting and controlled ball-handling drills",
    [
      focus("alternate stationary dribbling and close-range shots", "skill"),
      focus("dribble with each hand before a set shot", "skill"),
      focus("link a controlled change of direction to a shot", "complexity"),
      focus("use a short game-speed skill sequence with full control", "complexity"),
    ],
    { equipment: ["basketball"], preferredResources: ["sport-group", "outside"] },
  ),
  "beach-volleyball": activity(
    TECHNICAL,
    "serving, passing, or controlled rallies",
    [
      focus("practise controlled forearm passes to one target", "skill"),
      focus("alternate serving and passing practice", "skill"),
      focus("add movement before each controlled pass", "movement"),
      focus("link serve, transition, and controlled rally play", "complexity"),
    ],
    { equipment: ["volleyball"], preferredResources: ["outside", "sport-group"] },
  ),
  biking: activity(
    AEROBIC,
    "steady cycling",
    [
      focus("cycle steadily, then complete five minutes of gentle stretching", "recovery", { addOnMinutes: 5 }),
      focus("cycle at a low-to-moderate pace while keeping breathing steady", "attention", { intensityOverride: "low to moderate" }),
      focus("maintain one comfortable cadence for the full ride", "attention"),
      focus("use one small, controlled route or cadence change", "environment"),
    ],
    {
      equipment: ["bicycle or stationary bike"],
      preferredResources: ["outside", "gym", "equipment"],
    },
  ),
  billiards: activity(
    SOCIAL,
    "a casual game or focused shot practice",
    [
      focus("practise straight shots from a comfortable distance", "skill"),
      focus("alternate straight shots and simple angle shots", "skill"),
      focus("plan position for the next ball before each shot", "attention"),
      focus("play a short pattern with deliberate cue-ball control", "complexity"),
    ],
    { equipment: ["table", "cue", "balls"] },
  ),
  bocce: activity(
    SOCIAL,
    "a casual game or target-throwing practice",
    [
      focus("throw toward one comfortable target distance", "skill"),
      focus("alternate two target distances", "skill"),
      focus("practise placement around an existing ball", "skill"),
      focus("use a deliberate placement or displacement strategy", "complexity"),
    ],
    { equipment: ["bocce set"], preferredResources: ["grass-yard", "outside"] },
  ),
  bowling: activity(
    SOCIAL,
    "one relaxed game or controlled delivery practice",
    [
      focus("repeat one comfortable setup and delivery", "consistency"),
      focus("aim at one familiar lane target", "attention"),
      focus("adjust one starting position while keeping the same delivery", "skill"),
      focus("use a deliberate spare-target routine", "complexity"),
    ],
    { equipment: ["bowling lane", "ball"] },
  ),
  boxing: activity(
    COMBAT,
    "shadowboxing or supervised technique work",
    [
      focus("practise stance, guard, and straight punches", "skill"),
      focus("link simple footwork with a controlled combination", "complexity"),
      focus("add defensive movement after each combination", "complexity"),
      focus("complete controlled technical rounds with deliberate recovery", "attention"),
    ],
    { equipment: ["open space or bag"], supervision: "recommended" },
  ),
  "brazilian-jiu-jitsu": activity(
    COMBAT,
    "supervised positional or technique practice",
    [
      focus("repeat one familiar movement or positional escape", "skill"),
      focus("link two familiar positional movements", "complexity"),
      focus("use controlled positional transitions with a partner", "complexity"),
      focus("practise a familiar technical chain at a controlled pace", "complexity"),
    ],
    { participation: ["partner", "group"], supervision: "required" },
  ),
  broomball: activity(
    PLAY,
    "controlled skating, passing, or recreational play",
    [
      focus("practise balance, starts, and controlled stops", "skill"),
      focus("add short passing to a stationary target", "skill"),
      focus("link movement, receiving, and passing", "complexity"),
      focus("use controlled small-area play", "environment"),
    ],
    { equipment: ["broomball equipment"], participation: ["group"], supervision: "facility-rules" },
  ),
  cheerleading: activity(
    TECHNICAL,
    "familiar, supervised technique practice",
    [
      focus("practise familiar arm positions and timing", "skill"),
      focus("link familiar motions with controlled footwork", "complexity"),
      focus("practise a familiar sequence with consistent timing", "attention"),
      focus("refine one advanced familiar sequence under supervision", "skill"),
    ],
    { participation: ["group"], supervision: "required" },
  ),
  cornhole: activity(
    SOCIAL,
    "a casual game or target-throwing practice",
    [
      focus("repeat throws from one comfortable position", "consistency"),
      focus("aim for alternating board areas", "skill"),
      focus("practise controlled slide and direct throws", "skill"),
      focus("use a deliberate scoring or blocking strategy", "complexity"),
    ],
    { equipment: ["boards", "bags"], preferredResources: ["outside", "grass-yard"] },
  ),
  cricket: activity(
    TECHNICAL,
    "controlled batting, bowling, or fielding practice",
    [
      focus("practise catches and accurate short throws", "skill"),
      focus("focus on one batting or bowling fundamental", "skill"),
      focus("link fielding, transfer, and controlled return", "complexity"),
      focus("use a familiar multi-skill practice sequence", "complexity"),
    ],
    { equipment: ["ball", "bat or wickets"], preferredResources: ["grass-yard", "sport-group"] },
  ),
  "cross-country-skiing": activity(
    AEROBIC,
    "steady skiing on familiar terrain",
    [
      focus("keep the glide easy and rhythm consistent", "attention"),
      focus("coordinate steady breathing with the stride", "attention"),
      focus("use one small familiar terrain change", "environment"),
      focus("maintain a controlled cadence across varied familiar terrain", "complexity"),
    ],
    { equipment: ["skis", "poles", "appropriate trail"], preferredResources: ["outside"] },
  ),
  curling: activity(
    TECHNICAL,
    "delivery and sweeping practice or recreational play",
    [
      focus("repeat a comfortable delivery toward one target", "consistency"),
      focus("alternate draw weight between two familiar targets", "skill"),
      focus("coordinate delivery aim and controlled weight", "complexity"),
      focus("practise a short tactical end with familiar shots", "complexity"),
    ],
    { equipment: ["curling facility"], participation: ["social", "group"] },
  ),
  dancing: activity(
    CLASS,
    "a familiar dance sequence or moderate class",
    [
      focus("repeat one short familiar step sequence", "consistency"),
      focus("add coordinated arm movement to the same steps", "movement"),
      focus("link two familiar sequences without increasing pace", "complexity"),
      focus("refine timing and transitions in a familiar routine", "attention"),
    ],
    { preferredResources: ["indoor-space", "sport-group"] },
  ),
  darts: activity(
    SOCIAL,
    "a casual game or throwing practice",
    [
      focus("hit numbers 1–20 in sequence or continue casual throwing", "skill"),
      focus("play 501 independently with double-in and double-out, or throw casually", "skill"),
      focus("alternate scoring targets and familiar checkout numbers", "skill"),
      focus("practise deliberate checkout combinations in a 501 game", "complexity"),
    ],
    { equipment: ["dartboard", "darts"] },
  ),
  "disc-golf": activity(
    SOCIAL,
    "putting practice or a short recreational round",
    [
      focus("putt repeatedly from one comfortable distance", "consistency"),
      focus("alternate two familiar putting distances", "skill"),
      focus("add controlled approach shots to a target", "skill"),
      focus("play a short round with deliberate shot selection", "complexity"),
    ],
    { equipment: ["disc"], preferredResources: ["outside", "grass-yard"] },
  ),
  dodgeball: activity(
    PLAY,
    "controlled throwing and movement drills or casual play",
    [
      focus("practise accurate throws at a stationary target", "skill"),
      focus("add controlled side-to-side movement before throwing", "movement"),
      focus("link catching, movement, and an accurate return", "complexity"),
      focus("use controlled small-area recreational play", "environment"),
    ],
    { equipment: ["soft ball"], participation: ["partner", "group"] },
  ),
  "field-hockey": activity(
    TECHNICAL,
    "dribbling, passing, or shooting practice",
    [
      focus("dribble in a straight line with controlled touches", "skill"),
      focus("alternate dribbling and passing to a target", "skill"),
      focus("add a controlled direction change before passing", "complexity"),
      focus("link receiving, movement, and a targeted shot", "complexity"),
    ],
    { equipment: ["stick", "ball"], preferredResources: ["outside", "sport-group"] },
  ),
  "figure-skating": activity(
    TECHNICAL,
    "familiar edge work or controlled skating",
    [
      focus("practise forward glides and controlled stops", "skill"),
      focus("alternate familiar edges at an easy pace", "skill"),
      focus("link edge changes with controlled turns", "complexity"),
      focus("refine one familiar sequence with consistent posture", "attention"),
    ],
    { equipment: ["skates", "rink"], supervision: "facility-rules" },
  ),
  "flag-football": activity(
    PLAY,
    "route, passing, or non-contact recreational play",
    [
      focus("practise short passes and catches", "skill"),
      focus("add one controlled route before the catch", "movement"),
      focus("link route timing, catch, and controlled turn", "complexity"),
      focus("use a familiar small-sided non-contact play", "complexity"),
    ],
    { equipment: ["football", "flags"], preferredResources: ["outside", "sport-group"] },
  ),
  "floor-hockey": activity(
    TECHNICAL,
    "stickhandling, passing, or controlled play",
    [
      focus("stickhandle in a straight line with controlled touches", "skill"),
      focus("alternate stickhandling and passing to a target", "skill"),
      focus("add a direction change before passing", "complexity"),
      focus("link receiving, movement, and a controlled shot", "complexity"),
    ],
    { equipment: ["stick", "ball"], preferredResources: ["indoor-space", "sport-group"] },
  ),
  football: activity(
    TECHNICAL,
    "non-contact throwing, catching, or footwork practice",
    [
      focus("practise short throws and catches", "skill"),
      focus("add simple footwork before a controlled throw", "movement"),
      focus("link route timing and accurate delivery", "complexity"),
      focus("use a familiar non-contact multi-skill sequence", "complexity"),
    ],
    { equipment: ["football"], preferredResources: ["outside", "grass-yard"] },
  ),
  golf: activity(
    TECHNICAL,
    "putting, chipping, or controlled range practice",
    [
      focus("putt to one target from a comfortable distance", "skill"),
      focus("alternate putting and short chipping targets", "skill"),
      focus("use one club and repeat a consistent pre-shot routine", "attention"),
      focus("play a controlled practice sequence with deliberate shot selection", "complexity"),
    ],
    { equipment: ["clubs", "balls"], preferredResources: ["outside"] },
  ),
  "group-exercise": activity(
    CLASS,
    "one familiar moderate-intensity class",
    [
      focus("follow the familiar class while keeping transitions controlled", "attention"),
      focus("use the same class with a lighter movement option where useful", "movement"),
      focus("maintain steady breathing through familiar work periods", "attention"),
      focus("use one modest challenge while keeping the rest familiar", "intensity"),
    ],
    { preferredResources: ["gym", "sport-group"], participation: ["group"] },
  ),
  gymnastics: activity(
    TECHNICAL,
    "familiar, supervised fundamental practice",
    [
      focus("repeat one familiar body-position or landing drill", "skill"),
      focus("link two familiar floor fundamentals", "complexity"),
      focus("refine control through a familiar sequence", "attention"),
      focus("practise one advanced familiar element under supervision", "skill"),
    ],
    { equipment: ["appropriate facility"], supervision: "required" },
  ),
  hiking: activity(
    AEROBIC,
    "a steady walk on familiar terrain",
    [
      focus("walk an easy familiar route at a consistent pace", "consistency"),
      focus("keep breathing steady across gentle terrain changes", "attention"),
      focus("use one modest familiar incline without changing total time", "environment"),
      focus("maintain controlled pacing across a varied familiar route", "attention"),
    ],
    { preferredResources: ["outside", "safe-walk"] },
  ),
  horseshoes: activity(
    SOCIAL,
    "a casual game or target-throwing practice",
    [
      focus("repeat throws from one comfortable position", "consistency"),
      focus("alternate two familiar target distances", "skill"),
      focus("practise controlled placement around the stake", "skill"),
      focus("use a deliberate scoring or blocking strategy", "complexity"),
    ],
    { equipment: ["horseshoes", "stakes"], preferredResources: ["outside", "grass-yard"] },
  ),
  "ice-hockey": activity(
    TECHNICAL,
    "controlled skating, puck-handling, or shooting practice",
    [
      focus("practise controlled skating and stops", "skill"),
      focus("add stationary puck control or targeted passing", "skill"),
      focus("link skating, puck control, and a pass", "complexity"),
      focus("use a controlled multi-skill sequence at a familiar pace", "complexity"),
    ],
    { equipment: ["skates", "stick", "puck", "rink"], supervision: "facility-rules" },
  ),
  judo: activity(
    COMBAT,
    "supervised technique practice without hard sparring",
    [
      focus("repeat one familiar movement or entry", "skill"),
      focus("link entry, balance, and controlled finish", "complexity"),
      focus("alternate two familiar technical entries", "skill"),
      focus("practise a controlled technical chain with a partner", "complexity"),
    ],
    { participation: ["partner", "group"], supervision: "required" },
  ),
  karate: activity(
    COMBAT,
    "forms, footwork, or supervised technique practice",
    [
      focus("practise stance, guard, and one familiar technique", "skill"),
      focus("link simple footwork with a controlled combination", "complexity"),
      focus("repeat one familiar form with steady breathing", "attention"),
      focus("refine a familiar multi-technique sequence under supervision", "complexity"),
    ],
    { preferredResources: ["indoor-space", "sport-group"], supervision: "recommended" },
  ),
  kickball: activity(
    PLAY,
    "controlled kicking and fielding or casual play",
    [
      focus("practise controlled kicks to one target area", "skill"),
      focus("alternate kicking and easy fielding practice", "skill"),
      focus("link fielding, movement, and an accurate return", "complexity"),
      focus("use controlled small-sided recreational play", "environment"),
    ],
    { equipment: ["ball"], preferredResources: ["outside", "grass-yard", "sport-group"] },
  ),
  lacrosse: activity(
    TECHNICAL,
    "cradling, passing, or shooting practice",
    [
      focus("practise stationary cradling and short passes", "skill"),
      focus("add controlled movement before passing", "movement"),
      focus("link receiving, cradling, and a targeted pass", "complexity"),
      focus("use a familiar multi-skill sequence at a controlled pace", "complexity"),
    ],
    { equipment: ["stick", "ball"], preferredResources: ["outside", "sport-group"] },
  ),
  "lawn-games": activity(
    SOCIAL,
    "one familiar casual lawn game",
    [
      focus("repeat the easiest familiar version", "consistency"),
      focus("add one simple target or scoring focus", "skill"),
      focus("alternate two familiar game variations", "movement"),
      focus("use a short structured round with someone", "social"),
    ],
    { preferredResources: ["outside", "grass-yard"] },
  ),
  "martial-arts": activity(
    COMBAT,
    "supervised fundamentals or familiar technique practice",
    [
      focus("repeat stance, guard, and one familiar technique", "skill"),
      focus("link simple footwork with two familiar techniques", "complexity"),
      focus("add a controlled defensive movement", "complexity"),
      focus("practise a familiar technical sequence under supervision", "complexity"),
    ],
    { supervision: "recommended" },
  ),
  "mini-golf": activity(
    SOCIAL,
    "one relaxed recreational round",
    [
      focus("use a consistent setup before each putt", "attention"),
      focus("choose one target line before each putt", "skill"),
      focus("adjust pace for short and long putts", "skill"),
      focus("play a round with deliberate line and pace selection", "complexity"),
    ],
    { equipment: ["course", "putter", "ball"] },
  ),
  mma: activity(
    COMBAT,
    "supervised technical drilling without hard sparring",
    [
      focus("repeat stance, movement, and one familiar technique", "skill"),
      focus("link two familiar techniques at a controlled pace", "complexity"),
      focus("add a controlled transition between familiar positions", "complexity"),
      focus("practise a familiar multi-phase technical sequence", "complexity"),
    ],
    { participation: ["partner", "group"], supervision: "required" },
  ),
  "muay-thai": activity(
    COMBAT,
    "shadowboxing, footwork, or light bag work",
    [
      focus("practise stance, guard, and straight strikes", "skill"),
      focus("link controlled footwork with a short combination", "complexity"),
      focus("add one defensive movement after each combination", "complexity"),
      focus("complete controlled technical rounds with deliberate downshifting", "attention"),
    ],
    { equipment: ["open space or light bag"], supervision: "recommended" },
  ),
  pickleball: activity(
    TECHNICAL,
    "controlled rallies or serving practice",
    [
      focus("practise controlled serves to one target area", "skill"),
      focus("alternate serves and soft rally contact", "skill"),
      focus("add controlled movement toward the non-volley zone", "movement"),
      focus("use a serve, return, and third-shot practice pattern", "complexity"),
    ],
    { equipment: ["paddle", "ball", "court"], preferredResources: ["sport-group"] },
  ),
  racquetball: activity(
    TECHNICAL,
    "controlled rallies or wall practice",
    [
      focus("repeat forehand contact to one wall target", "skill"),
      focus("alternate forehand and backhand wall targets", "skill"),
      focus("add controlled court movement between shots", "movement"),
      focus("use a serve-and-rally practice sequence", "complexity"),
    ],
    { equipment: ["racquet", "ball", "court"] },
  ),
  ringette: activity(
    TECHNICAL,
    "controlled skating, passing, or shooting practice",
    [
      focus("practise controlled skating and stops", "skill"),
      focus("add stationary ring control or targeted passing", "skill"),
      focus("link skating, ring control, and a pass", "complexity"),
      focus("use a controlled multi-skill sequence at a familiar pace", "complexity"),
    ],
    { equipment: ["skates", "stick", "ring", "rink"], supervision: "facility-rules" },
  ),
  rowing: activity(
    AEROBIC,
    "steady rowing at a sustainable pace",
    [
      focus("keep the stroke smooth and consistent", "attention"),
      focus("coordinate steady breathing with the stroke", "attention"),
      focus("hold one comfortable stroke rate", "consistency"),
      focus("use one small controlled cadence change", "intensity"),
    ],
    { equipment: ["rowing machine or boat"], preferredResources: ["gym", "equipment", "water"] },
  ),
  rugby: activity(
    TECHNICAL,
    "non-contact passing, handling, or footwork practice",
    [
      focus("practise short passes and controlled catches", "skill"),
      focus("add simple footwork before passing", "movement"),
      focus("link movement, catch, and accurate pass", "complexity"),
      focus("use a familiar non-contact multi-skill sequence", "complexity"),
    ],
    { equipment: ["rugby ball"], preferredResources: ["outside", "grass-yard", "sport-group"] },
  ),
  running: activity(
    AEROBIC,
    "light jog",
    [
      focus("keep the route familiar and the pace conversational", "consistency"),
      focus("maintain relaxed arms and steady breathing", "attention"),
      focus("use a different familiar route at the same effort", "environment"),
      focus("add one brief controlled pace change without extending the run", "intensity"),
    ],
    { preferredResources: ["outside", "safe-walk", "gym", "equipment"] },
  ),
  skateboarding: activity(
    TECHNICAL,
    "familiar balance and rolling practice in a safe area",
    [
      focus("practise stance, balance, and controlled stops", "skill"),
      focus("link pushing, rolling, and stopping", "complexity"),
      focus("add familiar controlled turns in the same safe area", "skill"),
      focus("refine one familiar line without adding speed", "complexity"),
    ],
    { equipment: ["board", "appropriate protective equipment", "safe area"] },
  ),
  skiing: activity(
    AEROBIC,
    "controlled skiing on familiar terrain",
    [
      focus("use easy familiar turns and controlled stops", "skill"),
      focus("maintain steady breathing on one familiar run", "attention"),
      focus("vary turn shape without increasing speed", "skill"),
      focus("use one modest familiar terrain change at the same control", "environment"),
    ],
    { equipment: ["skis", "appropriate terrain"], supervision: "facility-rules" },
  ),
  snowboarding: activity(
    TECHNICAL,
    "controlled riding on familiar terrain",
    [
      focus("practise balanced gliding and controlled stops", "skill"),
      focus("link familiar turns without increasing speed", "complexity"),
      focus("vary turn shape on the same familiar run", "skill"),
      focus("use one modest familiar terrain change at the same control", "environment"),
    ],
    { equipment: ["snowboard", "appropriate terrain"], supervision: "facility-rules" },
  ),
  soccer: activity(
    TECHNICAL,
    "dribbling, passing, or shooting practice",
    [
      focus("dribble in a straight line with controlled touches", "skill"),
      focus("alternate dribbling and passing to a target", "skill"),
      focus("add a direction change before passing", "complexity"),
      focus("link receiving, movement, and a controlled shot", "complexity"),
    ],
    { equipment: ["soccer ball"], preferredResources: ["outside", "grass-yard", "sport-group"] },
  ),
  softball: activity(
    TECHNICAL,
    "throwing, catching, or batting practice",
    [
      focus("practise easy catches and accurate short throws", "skill"),
      focus("alternate fielding position and throwing target", "skill"),
      focus("link fielding, transfer, and controlled throw", "complexity"),
      focus("use a familiar batting or fielding sequence", "complexity"),
    ],
    { equipment: ["ball", "glove"], preferredResources: ["grass-yard", "sport-group"] },
  ),
  squash: activity(
    TECHNICAL,
    "controlled rallies or wall practice",
    [
      focus("repeat forehand contact to one wall target", "skill"),
      focus("alternate forehand and backhand wall targets", "skill"),
      focus("add controlled court movement between shots", "movement"),
      focus("use a serve-and-rally practice sequence", "complexity"),
    ],
    { equipment: ["racquet", "ball", "court"] },
  ),
  strength: activity(
    STRENGTH,
    "a familiar moderate full-body strength session",
    [
      focus("use a lower-body focus at the same overall demand", "movement"),
      focus("use an upper-body focus at the same overall demand", "movement"),
      focus("alternate upper- and lower-body movements with controlled rest", "complexity"),
      focus("add one familiar compound movement without increasing total demand", "complexity"),
    ],
    { equipment: ["bodyweight or familiar resistance"], preferredResources: ["gym", "equipment", "indoor-space"] },
  ),
  swimming: activity(
    AEROBIC,
    "easy continuous swimming or technique laps",
    [
      focus("use one familiar stroke at an easy steady pace", "consistency"),
      focus("focus on a smooth exhale and relaxed recovery", "attention"),
      focus("alternate easy continuous laps and technique laps", "movement"),
      focus("use one small controlled change in stroke or cadence", "complexity"),
    ],
    { equipment: ["pool or safe supervised water"], preferredResources: ["water"], supervision: "facility-rules" },
  ),
  "table-tennis": activity(
    TECHNICAL,
    "controlled rallies or serve practice",
    [
      focus("repeat forehand contact to one table area", "skill"),
      focus("alternate forehand and backhand targets", "skill"),
      focus("add controlled footwork between returns", "movement"),
      focus("use a serve, return, and third-ball practice pattern", "complexity"),
    ],
    { equipment: ["paddle", "ball", "table"] },
  ),
  taekwondo: activity(
    COMBAT,
    "forms, footwork, or supervised technique practice",
    [
      focus("practise stance, guard, and one familiar technique", "skill"),
      focus("link simple footwork with a controlled combination", "complexity"),
      focus("repeat one familiar form with steady breathing", "attention"),
      focus("refine a familiar multi-technique sequence under supervision", "complexity"),
    ],
    { preferredResources: ["indoor-space", "sport-group"], supervision: "recommended" },
  ),
  tennis: activity(
    TECHNICAL,
    "controlled rallies or serving practice",
    [
      focus("practise forehand contact to one target area", "skill"),
      focus("alternate forehand and backhand targets", "skill"),
      focus("add controlled footwork before each return", "movement"),
      focus("use a serve, return, and third-shot practice pattern", "complexity"),
    ],
    { equipment: ["racquet", "ball", "court"], preferredResources: ["sport-group", "outside"] },
  ),
  "track-field": activity(
    TECHNICAL,
    "familiar event drills at a controlled effort",
    [
      focus("repeat one familiar event fundamental", "skill"),
      focus("add a controlled approach or movement sequence", "complexity"),
      focus("use consistent setup and execution cues", "attention"),
      focus("link a familiar technical sequence without increasing total demand", "complexity"),
    ],
    { preferredResources: ["outside", "sport-group"], supervision: "recommended" },
  ),
  "ultimate-frisbee": activity(
    TECHNICAL,
    "throwing, catching, or controlled recreational play",
    [
      focus("practise short throws and catches", "skill"),
      focus("alternate forehand and backhand throws", "skill"),
      focus("add controlled movement before the catch and return", "movement"),
      focus("use a familiar small-sided passing sequence", "complexity"),
    ],
    { equipment: ["disc"], preferredResources: ["outside", "grass-yard", "sport-group"] },
  ),
  volleyball: activity(
    TECHNICAL,
    "serving, passing, or controlled skill practice",
    [
      focus("practise controlled forearm passes to one target", "skill"),
      focus("alternate serving and passing practice", "skill"),
      focus("add movement before each controlled pass", "movement"),
      focus("link serve, transition, and controlled rally play", "complexity"),
    ],
    { equipment: ["volleyball"], preferredResources: ["sport-group", "outside"] },
  ),
  walking: activity(
    AEROBIC,
    "a comfortable purposeful walk",
    [
      focus("use a different familiar route at the same pace", "environment"),
      focus("walk with relaxed arm movement and steady breathing", "attention"),
      focus("maintain one comfortable pace from start to finish", "consistency"),
      focus("use one modest familiar terrain change without extending the walk", "environment"),
    ],
    {
      preferredResources: ["outside", "safe-walk", "indoor-space"],
      environmentProgressions: [
        environment(["outside", "safe-walk"], "progression-1", focus("walk a different familiar route at the same pace", "environment")),
        environment(["indoor-space", "gym"], "progression-1", focus("use a different familiar indoor route at the same pace", "environment")),
      ],
    },
  ),
  "water-polo": activity(
    TECHNICAL,
    "controlled swimming, passing, or shooting practice",
    [
      focus("practise controlled passing from a stable position", "skill"),
      focus("alternate easy swimming and targeted passing", "movement"),
      focus("link movement, receiving, and a controlled pass", "complexity"),
      focus("use a familiar multi-skill sequence at a controlled pace", "complexity"),
    ],
    { equipment: ["pool", "ball"], preferredResources: ["water", "sport-group"], supervision: "facility-rules" },
  ),
  wrestling: activity(
    COMBAT,
    "supervised technique or positional practice",
    [
      focus("repeat one familiar stance or movement drill", "skill"),
      focus("link stance, movement, and one familiar entry", "complexity"),
      focus("use controlled positional transitions with a partner", "complexity"),
      focus("practise a familiar technical chain at a controlled pace", "complexity"),
    ],
    { participation: ["partner", "group"], supervision: "required" },
  ),
  yoga: activity(
    RECOVERY,
    "a familiar controlled sequence",
    [
      focus("focus on hips and lower-body mobility", "movement"),
      focus("focus on shoulders, upper back, and chest", "movement"),
      focus("link familiar positions with steady breathing", "attention"),
      focus("use a controlled full-body sequence with smooth transitions", "complexity"),
    ],
    { preferredResources: ["indoor-space", "sport-group"] },
  ),
  other: activity(
    PLAY,
    "the familiar version you selected",
    [
      focus("repeat one simple familiar skill", "skill"),
      focus("add one attention or breathing focus", "attention"),
      focus("use one small movement variation", "movement"),
      focus("use one controlled complexity change", "complexity"),
    ],
  ),
};

const VALID_LEVELS = ["beginner", "intermediate", "advanced"];
const VALID_CONDITIONING = ["low", "moderate", "high"];

export const inferActivitySkillLevel = (schedule, activityKey) => {
  const explicit = schedule.activitySkillLevels?.[activityKey];
  if (VALID_LEVELS.includes(explicit)) return explicit;
  if (schedule.activityIntent === "regular") return "intermediate";
  return "beginner";
};

export const inferConditioningLevel = (profileKey, current, schedule) => {
  const explicit = schedule.conditioningLevel;
  if (VALID_CONDITIONING.includes(explicit)) return explicit;
  if (
    ["pause", "modify"].includes(current.physicalReadiness) ||
    Number(current.energy) <= 2 ||
    ["reduced-capacity", "recovery-focus"].includes(profileKey)
  ) {
    return "low";
  }
  if (
    profileKey === "growth-performance" &&
    current.physicalReadiness === "unrestricted" &&
    (schedule.familiarActivities || []).some((item) =>
      ["aerobic", "resistance", "anaerobic", "sport-competition"].includes(item),
    )
  ) {
    return "high";
  }
  return "moderate";
};

export const getProgressionStage = (dayOffset) =>
  WEEKLY_PROGRESSION_SEQUENCE[Math.max(0, Math.min(6, dayOffset - 1))] ||
  WEEKLY_PROGRESSION_SEQUENCE[0];

const matchingEnvironmentProgression = (entry, resources, stage) =>
  (entry.environmentProgressions || []).find(
    (item) =>
      item.stage === stage &&
      item.requiresAny.some((resource) => resources.includes(resource)),
  )?.variation;

export const getActivityVariation = ({
  activityKey,
  skillLevel,
  stage,
  resources = [],
}) => {
  const entry =
    ACTIVITY_PROGRESSION_REPOSITORY[activityKey] ||
    ACTIVITY_PROGRESSION_REPOSITORY.other;
  if (stage === "base") return entry.base;

  const environmentVariation = matchingEnvironmentProgression(
    entry,
    resources,
    stage,
  );
  if (environmentVariation) return environmentVariation;

  const level = VALID_LEVELS.includes(skillLevel) ? skillLevel : "beginner";
  const index = stage === "progression-2" ? 1 : 0;
  const selected = entry.levels[level][index] || entry.base;
  const preferredResources = entry.requirements.preferredResources || [];
  const environmentAvailable =
    preferredResources.length === 0 ||
    preferredResources.some((resource) => resources.includes(resource));
  if (selected.variable !== "environment" || environmentAvailable) {
    return selected;
  }
  return (
    entry.levels[level].find(
      (variation) => variation.variable !== "environment",
    ) || entry.base
  );
};

export const repositoryCoverage = () =>
  Object.keys(ACTIVITY_PROGRESSION_REPOSITORY);
